import React from 'react';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, XCircle, Upload, Mic, Zap, Database } from 'lucide-react';
import { ProcessingStatus as ProcessingStatusType } from '../types';

interface ProcessingStatusProps {
  status: ProcessingStatusType;
}

const ProcessingStatus: React.FC<ProcessingStatusProps> = ({ status }) => {
  const getStatusIcon = () => {
    switch (status.status) {
      case 'uploading':
        return <Upload className="animate-pulse" size={24} />;
      case 'transcribing':
        return status.message.includes('cache') ? 
          <Database className="animate-pulse" size={24} /> : 
          <Mic className="animate-pulse" size={24} />;
      case 'processing':
        return <Zap className="animate-pulse" size={24} />;
      case 'completed':
        return status.message.includes('cache') ?
          <Database className="text-green-600" size={24} /> :
          <CheckCircle className="text-green-600" size={24} />;
      case 'error':
        return <XCircle className="text-red-600" size={24} />;
      default:
        return <Loader2 className="animate-spin" size={24} />;
    }
  };

  const getStatusColor = () => {
    switch (status.status) {
      case 'completed':
        return status.message.includes('cache') ? 'text-blue-600' : 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-primary-600';
    }
  };

  const getBackgroundColor = () => {
    switch (status.status) {
      case 'completed':
        return status.message.includes('cache') ? 'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-700' : 'bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-700';
      case 'error':
        return 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700';
      default:
        return 'bg-primary-50 dark:bg-primary-900 border-primary-200 dark:border-primary-700';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`border rounded-xl p-6 ${getBackgroundColor()}`}
    >
      <div className="flex items-center space-x-4">
        <div className={getStatusColor()}>
          {getStatusIcon()}
        </div>
        
        <div className="flex-1">
          <div className={`font-medium ${getStatusColor()}`}>
            {status.message}
          </div>
          
          {status.progress !== undefined && (
            <div className="mt-3">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
                <span>Progress</span>
                <span>{status.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <motion.div
                  className={`h-2 rounded-full ${
                    status.status === 'error' 
                      ? 'bg-red-500' 
                      : status.message.includes('cache')
                      ? 'bg-blue-500'
                      : 'bg-primary-500'
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${status.progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          )}

          {status.message.includes('cache') && status.status === 'completed' && (
            <div className="mt-2 text-sm text-blue-600 dark:text-blue-400 flex items-center space-x-1">
              <Database size={14} />
              <span>⚡ Instant result from cache - no API call needed!</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProcessingStatus; 